import { action, computed, makeObservable, observable } from 'mobx';
import { environmentSvc, EnvironmentType } from '../common/environment.service';
import { ModuleEventFactory, ModuleEventName } from '../common/events/module-event';
import { OutboundDomEventSource } from '../common/events/outbound-dom-events';
import { ExternalName } from '../common/general/module/brand-loader';
import { preconnectToApiUrl } from '../embedding/assets-injection';
import { Brand, DWTComponentOptions, ModuleSpecificOptions } from '../embedding/dwt-app';
import { PageType } from '../admin/vacs-admin-manual-vehicles/vacs-admin-manual-vehicle.model';

const brands: Brand[] = [
  'opel',
  'opelold',
  'peugeot',
  'ds',
  'citroen',
  'ssangyong',
  'isuzu',
  'fiat',
  'jeep',
  'alfaromeo',
  'abarth',
  'lancia',
];

const brandsMap = {
  OPEL: 'opel',
  OPELOLD: 'opelold',
  PEUGEOT: 'peugeot',
  CITROEN: 'citroen',
  DS: 'ds',
  SSANGYONG: 'ssangyong',
  ISUZU: 'isuzu',
  FIAT: 'fiat',
  JEEP: 'jeep',
  ALFA: 'alfaromeo',
  ABARTH: 'abarth',
  PETRANYI: 'petranyi',
  LANCIA: 'lancia',
};

const tinyMCEditorAoiKeys = {
  local: 'T7LK77EF99E1987BDFBEF74E0BB194ED6729818864661B89715C37544D521770',
  dev: 'T7LK77EF99E1987BDFBEF74E0BB194ED6729818864661B89715C37544D521770',
  uat: 'T7LK77EF99E1987BDFBEF74E0BB194ED6729818864661B89715C37544D521770',
  staging: 'T7LK77EF99E1987BDFBEF74E0BB194ED6729818864661B89715C37544D521770',
  prod: 'T7LKF19DA2E14B233C26745CBD12BA440FD69E38541B840DACAD1F5FABD631B1',
};

export class EmbedStore {
  public apiKey: string | undefined;
  public additionalQueryParams: { key: string; value: any }[] | undefined;

  public setting: string | undefined;

  public customEnvironment: EnvironmentType = 'dev';

  public customHeaders: Record<string, string> | undefined;

  public brand: Brand | undefined;

  public brandsMap: any | undefined;
  public permissions: string[] | undefined;
  public jwtToken: any | undefined;
  public isCountrySite = false;
  public decodedJwtToken: any | undefined;

  public loginUrl = '';

  public moduleName: ExternalName | undefined;

  public apiUrl = '';

  public adminMode = false;
  public pageType = PageType.BULK;
  public useVacs = false;
  public isCmsAvailable = false;
  public previewMode = false;

  public shiroSessionId?: string | undefined;
  public amAuthUserSessionId?: string | undefined;
  public callbackFnName?: string | undefined;
  public tooltipContainerId = '';

  public customData: any;
  public widgetData: any;

  public moduleSpecificOptions: ModuleSpecificOptions = {};

  constructor() {
    makeObservable(this, {
      apiKey: observable,
      previewMode: observable,
      setting: observable,
      customEnvironment: observable,
      shiroSessionId: observable,
      amAuthUserSessionId: observable,
      customHeaders: observable,
      tooltipContainerId: observable,
      isCountrySite: observable,
      brand: observable,
      customData: observable,
      widgetData: observable,
      loginUrl: observable,
      moduleName: observable,
      apiUrl: observable,
      moduleSpecificOptions: observable,
      moduleId: computed,
      moduleVariantId: computed,
      outboundDomEventSource: computed,
      init: action,
      setIsCountrySite: action,
      pageType: observable,
    });
  }

  public setIsCountrySite(value: boolean) {
    this.isCountrySite = value;
  }

  public get moduleId() {
    return `${this.moduleName}_${this.apiKey}`;
  }

  public get moduleVariantId() {
    return `${this.moduleName}_${this.apiKey}_${this.setting}`;
  }

  public get outboundDomEventSource(): OutboundDomEventSource {
    return {
      module: this.moduleName as ExternalName,
      moduleApiKey: this.apiKey ?? '',
      moduleSettings: this.setting ?? '',
    };
  }

  public get tinyMCEditorApiKey() {
    return tinyMCEditorAoiKeys[this.customEnvironment];
  }

  public createModuleEvent: ModuleEventFactory = <D>(name: ModuleEventName, data: D) => ({
    name,
    data,
    apiKey: this.apiKey ?? '',
    setting: this.setting ?? '',
    module: this.moduleName ?? ('' as ExternalName),
    shiroSessionId: this.shiroSessionId ?? '',
    amAuthUserSessionId: this.amAuthUserSessionId ?? '',
    brand: this.brand,
    customData: this.customData,
    widgetData: this.widgetData,
    useVacs: this.customData?.useVacs || false,
    isCmsAvailable: this.customData?.isCmsAvailable || false,
  });

  private generateShortId() {
    return Math.random().toString(36).substring(2, 8);
  }

  public init(options: DWTComponentOptions, moduleName: ExternalName) {
    if (!options) {
      return;
    }
    const {
      apiKey,
      setting,
      brand,
      customHeaders,
      adminMode,
      pageType,
      shiroSessionId,
      amAuthUserSessionId,
      callbackFnName,
      customData,
      widgetData,
      additionalQueryParams,
      previewMode,
      customEnvironment,
      ...moduleSpecificOptions
    } = options;

    this.moduleName = moduleName;
    this.apiKey = apiKey;
    this.additionalQueryParams = additionalQueryParams;
    this.setting = setting;
    this.moduleSpecificOptions = moduleSpecificOptions;
    this.customHeaders = customHeaders;
    this.brandsMap = brandsMap;
    this.shiroSessionId = shiroSessionId;
    this.amAuthUserSessionId = amAuthUserSessionId;
    this.callbackFnName = callbackFnName;
    this.widgetData = widgetData;
    this.customData = customData && typeof customData === 'string' ? JSON.parse(customData ?? {}) : customData;
    this.useVacs = this.customData?.useVacs || false;
    this.previewMode = previewMode || false;
    this.isCmsAvailable = this.customData?.isCmsAvailable || false;
    this.customEnvironment = customEnvironment;
    this.loginUrl = environmentSvc.getLoginUrl(this.customEnvironment, adminMode);
    this.apiUrl = environmentSvc.getApiUrl(this.customEnvironment);
    this.adminMode = adminMode!;
    this.pageType = pageType !== undefined ? pageType : PageType.BULK;
    this.tooltipContainerId = options.tooltipContainerId ? options.tooltipContainerId : this.generateShortId();
    preconnectToApiUrl(this.apiUrl);

    if (brand && brands.includes(brand)) {
      this.brand = brand;
    }
  }
}
